import { Controller } from "@hotwired/stimulus"

/**
 * AutocompleteController
 * 
 * Provides autocomplete functionality for input fields without jQuery dependency.
 * Fetches suggestions from a server endpoint and displays them in a dropdown.
 * 
 * @example
 *   <input type="text" 
 *          data-controller="autocomplete"
 *          data-autocomplete-url="/search/suggestions">
 */
export default class extends Controller {
  static values = {
    url: String,
    minLength: { type: Number, default: 3 },
    paramName: { type: String, default: 'term' }
  }

  connect() {
    this.resultsContainer = null
    this.selectedIndex = -1
    this.results = []
    this.debounceTimer = null

    this.onInput = this.onInput.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onScroll = this.updatePosition.bind(this)
    this.onResize = this.updatePosition.bind(this)
    this.onClickOutside = this.onClickOutside.bind(this)

    this.element.addEventListener('input', this.onInput)
    this.element.addEventListener('keydown', this.onKeydown)
    this.element.setAttribute('autocomplete', 'off')
  }

  disconnect() {
    this.hideResults()
    clearTimeout(this.debounceTimer)
    window.removeEventListener('scroll', this.onScroll, true)
    window.removeEventListener('resize', this.onResize)
  }

  onInput(event) {
    const query = event.target.value.trim()
    
    clearTimeout(this.debounceTimer)
    
    if (query.length < this.minLengthValue) {
      this.hideResults()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 200)
  }

  onKeydown(event) {
    if (!this.resultsContainer) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectNext()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectPrevious()
        break
      case 'Enter':
        if (this.selectedIndex >= 0) {
          event.preventDefault()
          this.selectResult(this.results[this.selectedIndex])
        }
        break
      case 'Escape':
        this.hideResults()
        break
    }
  }

  async fetchResults(query) {
    const url = this.urlValue || this.element.dataset.autocompleteUrl
    if (!url) return

    try {
      const paramName = this.paramNameValue
      const response = await fetch(`${url}?${paramName}=${encodeURIComponent(query)}`, {
        headers: { 'Accept': 'application/json' }
      })
      
      if (!response.ok) return
      
      const data = await response.json()
      
      // Support both array response and {query, results} object
      if (Array.isArray(data)) {
        this.results = data
        this.searchQuery = query
      } else {
        this.results = data.results || []
        this.searchQuery = data.query || query
      }
      
      this.showResults()
    } catch (error) {
      console.error('Autocomplete fetch error:', error)
    }
  }

  showResults() {
    if (this.results.length === 0) {
      this.hideResults()
      return
    }

    if (!this.resultsContainer) {
      this.resultsContainer = document.createElement('ul')
      this.resultsContainer.className = 'autocomplete-results'
      
      // Append to body to escape overflow:hidden containers
      document.body.appendChild(this.resultsContainer)
      
      // Listen for scroll/resize to reposition
      window.addEventListener('scroll', this.onScroll, true)
      window.addEventListener('resize', this.onResize)
      document.addEventListener('click', this.onClickOutside)
    }

    this.updatePosition()

    this.selectedIndex = -1
    this.resultsContainer.innerHTML = this.results.map((result, index) => {
      if (typeof result === 'string') {
        return `<li class="autocomplete-item" data-index="${index}">${this.highlightMatch(result)}</li>`
      }
      
      const label = result.label || result.value || result.name
      const sublabel = result.sublabel || result.description || null
      const meta = result.meta || null
      const image = result.image || null
      
      if (image || sublabel) {
        return `<li class="autocomplete-item autocomplete-item--rich" data-index="${index}">
          ${image ? `<img class="autocomplete-item__image" src="${this.escapeHtml(image)}" alt="" loading="lazy" />` : ''}
          <div class="autocomplete-item__content">
            <span class="autocomplete-item__label">${this.highlightMatch(label)}</span>
            ${sublabel ? `<span class="autocomplete-item__sublabel">${this.highlightMatch(sublabel)}</span>` : ''}
            ${meta ? `<span class="autocomplete-item__meta">From ${this.escapeHtml(meta)}</span>` : ''}
          </div>
        </li>`
      }
      
      return `<li class="autocomplete-item" data-index="${index}">${this.highlightMatch(label)}</li>`
    }).join('')

    this.resultsContainer.querySelectorAll('.autocomplete-item').forEach(item => {
      item.addEventListener('mouseenter', () => {
        this.selectedIndex = parseInt(item.dataset.index)
        this.updateSelection()
      })
      item.addEventListener('click', () => {
        this.selectResult(this.results[parseInt(item.dataset.index)])
      })
    })
  }

  hideResults() {
    if (this.resultsContainer) {
      this.resultsContainer.remove()
      this.resultsContainer = null
      window.removeEventListener('scroll', this.onScroll, true)
      window.removeEventListener('resize', this.onResize)
      document.removeEventListener('click', this.onClickOutside)
    }
    this.selectedIndex = -1
    this.results = []
  }

  onClickOutside(event) {
    if (!this.resultsContainer) return
    
    const clickedInsideInput = this.element.contains(event.target)
    const clickedInsideResults = this.resultsContainer.contains(event.target)
    
    if (!clickedInsideInput && !clickedInsideResults) {
      this.hideResults()
    }
  }

  updatePosition() {
    if (!this.resultsContainer) return
    
    const rect = this.element.getBoundingClientRect()
    const dropdownWidth = 400 // Fixed width for rich results
    
    // Position dropdown to extend left from the right edge of the input
    const rightEdge = rect.right
    const leftPosition = Math.max(10, rightEdge - dropdownWidth)
    
    this.resultsContainer.style.top = `${rect.bottom + 4}px`
    this.resultsContainer.style.left = `${leftPosition}px`
    this.resultsContainer.style.width = `${dropdownWidth}px`
  }

  selectNext() {
    if (this.results.length === 0) return
    this.selectedIndex = (this.selectedIndex + 1) % this.results.length
    this.updateSelection()
  }

  selectPrevious() {
    if (this.results.length === 0) return
    this.selectedIndex = this.selectedIndex <= 0 ? this.results.length - 1 : this.selectedIndex - 1
    this.updateSelection()
  }

  updateSelection() {
    if (!this.resultsContainer) return
    
    this.resultsContainer.querySelectorAll('.autocomplete-item').forEach((item, index) => {
      item.classList.toggle('is-selected', index === this.selectedIndex)
    })
  }

  selectResult(result) {
    const value = typeof result === 'string' ? result : (result.value || result.label || result.name)
    this.element.value = value
    this.hideResults()
    
    // Dispatch event for other controllers to react
    this.element.dispatchEvent(new CustomEvent('autocomplete:selected', { 
      detail: { result },
      bubbles: true 
    }))

    const url = typeof result === 'object' ? (result.product_link) : null
    if (url) {
      window.location.href = url
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  highlightMatch(text) {
    if (!this.searchQuery || !text) {
      return this.escapeHtml(text)
    }
    
    const escaped = this.escapeHtml(text)
    const query = this.searchQuery.trim()
    
    if (!query) return escaped
    
    // Escape regex special characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')
    
    return escaped.replace(regex, '<strong class="search-match">$1</strong>')
  }
}
